import React, { useState, useMemo } from 'react';
import { Drawer } from 'antd';
import { Flex } from 'antd-v5';
import PropTypes from 'prop-types';

import OnTraccrButton from '../common/buttons/OnTraccrButton';
import DrawerFooter from '../common/containers/DrawerFooter';
import OnTraccrCheckbox from '../common/inputs/OnTraccrCheckbox';
import { getEquipmentCustomFields } from './projectExport';
import { FOOTER_HEIGHT } from '../constants/Styles';
import { BUCKET_TEMPLATE_PREFIX } from './projects.constants';

const styles = {
  drawerBodyStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    marginBottom: FOOTER_HEIGHT,
  },
  textStyle: {
    margin: 0,
    fontWeight: 'bold',
    fontSize: 14,
  },
};

export const EXPORT_PROJECT_COLUMN_OPTIONS = [
  { key: 'name', labelKey: 'Name', required: true },
  { key: 'number', labelKey: 'Number', required: false },
  { key: 'address', labelKey: 'Address', required: false },
  { key: 'division', labelKey: 'Division', required: false },
  { key: 'customer', labelKey: 'Customer', required: false },
];

export const LABEL_COLUMN_OPTION = { key: 'labels', labelKey: 'Labels', required: false };

export const EXPORT_EQUIPMENT_COLUMN_OPTIONS = [
  { key: 'id', labelKey: 'ID', required: false },
  { key: 'name', labelKey: 'Name', required: false },
  { key: 'status', labelKey: 'Status', required: false },
  { key: 'labels', labelKey: 'Labels', required: false },
  { key: 'buckets', labelKey: 'Buckets', required: false },
  { key: 'vendor', labelKey: 'Vendor', required: false },
];

export const getExportEquipmentColumnOptions = (equipmentTypes = []) => {
  const customCols = getEquipmentCustomFields(equipmentTypes).map(
    ({ key, title }) => ({ key, labelKey: title, required: false }),
  );
  return [...EXPORT_EQUIPMENT_COLUMN_OPTIONS, ...customCols];
};

const DEFAULT_PROJECT_COLUMN_KEYS = ['name', 'number', 'address', 'division', 'customer', 'labels'];
const DEFAULT_EQUIPMENT_COLUMN_KEYS = ['id', 'name', 'status', 'labels', 'buckets', 'vendor'];

export default function ProjectExportDrawer({
  visible,
  onClose,
  onExport,
  equipmentTypes = [],
  bucketTemplateProjectMap,
}) {
  const [projectKeys, setProjectKeys] = useState(DEFAULT_PROJECT_COLUMN_KEYS);
  const [includeEquipment, setIncludeEquipment] = useState(false);
  const [equipmentColumnKeys, setEquipmentColumnKeys] = useState(DEFAULT_EQUIPMENT_COLUMN_KEYS);
  const [prevVisible, setPrevVisible] = useState(visible);

  const equipmentColumnOptions = getExportEquipmentColumnOptions(equipmentTypes);

  const projectColumnSet = new Set(projectKeys);
  const equipmentColumnSet = new Set(equipmentColumnKeys);

  const handleToggle = (key) => (checked) => {
    setProjectKeys((prev) => (
      checked
        ? [...prev, key]
        : prev.filter((k) => k !== key)
    ));
  };

  const handleEquipmentToggle = (key) => (checked) => {
    setEquipmentColumnKeys((prev) => (
      checked
        ? [...prev, key]
        : prev.filter((k) => k !== key)
    ));
  };

  const handleExportClick = () => {
    if (onExport) {
      onExport({
        projectColumnKeys: projectKeys,
        includeEquipment,
        equipmentColumnKeys,
      });
    }
    onClose();
  };

  const bucketTemplateColumnOptions = useMemo(() => (
    Object.keys(bucketTemplateProjectMap)
      .map((bucketTemplateId) => ({
        key: `${BUCKET_TEMPLATE_PREFIX}${bucketTemplateId}`,
        labelKey: bucketTemplateProjectMap[bucketTemplateId].name,
      }))
  ), [bucketTemplateProjectMap]);

  if (prevVisible !== visible) {
    setPrevVisible(visible);
    setProjectKeys(
      DEFAULT_PROJECT_COLUMN_KEYS.concat(bucketTemplateColumnOptions.map(({ key }) => key)),
    );
    setEquipmentColumnKeys(DEFAULT_EQUIPMENT_COLUMN_KEYS);
    setIncludeEquipment(false);
  }

  return (
    <Drawer
      title="Export Projects"
      visible={visible}
      onClose={onClose}
      width={400}
      bodyStyle={styles.drawerBodyStyle}
    >
      <p style={{ marginBottom: 0 }}>
        Use the following options to customize the export and include the information you need.
      </p>
      <Flex vertical gap={8}>
        <p style={styles.textStyle}>Project Columns:</p>
        <Flex vertical gap={4}>
          {EXPORT_PROJECT_COLUMN_OPTIONS
            .concat(bucketTemplateColumnOptions)
            .concat(LABEL_COLUMN_OPTION)
            .map(({ key, labelKey, required }) => (
              <OnTraccrCheckbox
                key={key}
                value={projectColumnSet.has(key)}
                onChange={handleToggle(key)}
                disabled={required}
                label={labelKey}
              />
            ))}
        </Flex>
      </Flex>
      <Flex vertical gap={8}>
        <OnTraccrCheckbox
          value={includeEquipment}
          onChange={(checked) => setIncludeEquipment(checked)}
          label="Include project equipment info?"
          textStyle={{ fontWeight: 'bold' }}
        />
        {includeEquipment && (
          <Flex vertical gap={4}>
            <p style={styles.textStyle}>Equipment Columns:</p>
            <Flex vertical gap={4}>
              {equipmentColumnOptions.map(({ key, labelKey }) => (
                <OnTraccrCheckbox
                  key={key}
                  value={equipmentColumnSet.has(key)}
                  onChange={handleEquipmentToggle(key)}
                  label={labelKey}
                />
              ))}
            </Flex>
          </Flex>
        )}
      </Flex>
      <DrawerFooter className="drawer-footer">
        <Flex justify="end" gap={8}>
          <OnTraccrButton
            title="Cancel"
            type="cancel"
            onClick={onClose}
          />
          <OnTraccrButton
            title="Export"
            onClick={handleExportClick}
          />
        </Flex>
      </DrawerFooter>
    </Drawer>
  );
}

ProjectExportDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExport: PropTypes.func,
  equipmentTypes: PropTypes.arrayOf(PropTypes.shape({})),
  bucketTemplateProjectMap: PropTypes.objectOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      projectMap: PropTypes.objectOf(
        PropTypes.arrayOf(PropTypes.string),
      ).isRequired,
    }),
  ),
};

ProjectExportDrawer.defaultProps = {
  onExport: undefined,
  equipmentTypes: [],
  bucketTemplateProjectMap: {},
};
